<template>
  <BaseModal v-if="!confirmationOpen" modal-class="change-diet-dates-modal" @close="emit('close')">
    <template #title>
      <div class="change-diet-dates-modal__title">
        <BaseIcon
          class="change-diet-dates-modal__title-icon"
          name="calendar_month"
          type="secondary"
        />
        <span>Zaplanuj daty jadłospisu</span>
      </div>
    </template>
    <div class="change-diet-dates-modal__body">
      <p class="change-diet-dates-modal__description">
        Ustalenie daty nie spowoduje wysłania jadłospisu do pacjenta.
      </p>
      <div class="change-diet-dates-modal__date-row">
        <div class="change-diet-dates-modal__date-picker-wrap">
          <DatePicker
            v-model="modalStartDate"
            label="Wybierz datę startu"
            name="modalStartDate"
            :disabled="allowPatientToChooseStartDate || isFetchingPublishDates"
            :disabled-dates="isDateDisabled"
            :get-date-status="getDateStatus"
            :selection-span-in-days="selectionSpanInDays"
            inline
          />
          <div v-if="isFetchingPublishDates" class="change-diet-dates-modal__loader-overlay">
            <BaseLoader class="base-loader--size-24" />
          </div>
          <CalendarLegend class="change-diet-dates-modal__legend" />
        </div>
      </div>
      <div class="change-diet-dates-modal__switch-row">
        <BaseSwitch
          :value="allowPatientToChooseStartDate"
          @click="allowPatientToChooseStartDate = $event"
        />
        <span class="change-diet-dates-modal__switch-label">
          Pozwól pacjentowi wybrać datę startu
        </span>
      </div>
    </div>
    <template #actions>
      <Flex :align-x="allowPatientToChooseStartDate ? 'right' : 'between'" :gap="12" flex="fluid">
        <StyledButton v-if="!allowPatientToChooseStartDate" variant="secondary" @click="clearDates">
          Wyczyść daty
        </StyledButton>
        <Flex :gap="12">
          <StyledButton variant="outline" @click="emit('close')">Anuluj</StyledButton>
          <StyledButton :disabled="mutation.isPending.value" @click="save">Zapisz</StyledButton>
          <BaseLoader v-if="mutation.isPending.value" class="base-loader--size-12" />
        </Flex>
      </Flex>
    </template>
  </BaseModal>
  <BaseModal v-else modal-class="undo-share-diet-modal" @close="confirmationOpen = false">
    <template #title>Cofnąć udostępnienie jadłospisu?</template>
    <p class="change-diet-dates-modal__warning-description">
      Ten jadłospis jest już udostępniony pacjentowi. Wyczyszczenie dat spowoduje cofnięcie
      udostępnienia i zniknięcie jadłospisu z aplikacji.
    </p>
    <template #actions>
      <StyledButton variant="outline" @click="confirmationOpen = false">Anuluj</StyledButton>
      <StyledButton :disabled="mutation.isPending.value" @click="mutation.mutate(true)">
        Potwierdź
      </StyledButton>
      <BaseLoader v-if="mutation.isPending.value" class="base-loader--size-12" />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { G } from '@mobily/ts-belt'
  import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
  import { format } from 'date-fns'
  import { computed, ref } from 'vue'

  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseLoader from '@/components/BaseLoader.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import BaseSwitch from '@/components/BaseSwitch.vue'
  import { DatePicker } from '@/components/DatePicker'
  import { StyledButton } from '@/components/StyledButton'
  import Flex from '@/components/ui/Flex/Flex.vue'
  import { CalendarLegend } from '@/pages/diets/components/CalendarLegend'
  import { dietsService } from '@/services/dietsService'
  import { useGlobalStore } from '@/store/globalStore'
  import {
    getMealPlanDateStatuses,
    type MealPlanDateStatus,
    toMealPlanDateKey,
  } from '@/utils/getMealPlanDateStatuses'
  import { reportError } from '@/utils/reportError'

  type PublishDatesResponse = { dates: { date: number; status: MealPlanDateStatus }[] }

  const props = defineProps<{
    daysCount: number
    dietId: number
    isSharedWithPatient?: boolean
    startDate?: Date | null
  }>()
  const emit = defineEmits<{ close: []; success: [] }>()
  const queryClient = useQueryClient()
  const globalStore = useGlobalStore()
  const allowPatientToChooseStartDate = ref(false)
  const confirmationOpen = ref(false)
  const modalStartDate = ref<Date | undefined>(props.startDate ?? undefined)

  const { data: publishDatesData, isFetching: isFetchingPublishDates } =
    useQuery<PublishDatesResponse>({
      queryFn: async () => {
        try {
          const response = await dietsService.getBlockedDates(props.dietId)
          return { dates: response.data.dates }
        } catch (error) {
          reportError(error, 'Error while fetching blocked dates', {
            dietId: props.dietId,
          })
          return { dates: [] }
        }
      },
      queryKey: ['blockedDates', props.dietId],
      retry: false,
    })

  const selectionSpanInDays = computed(() => props.daysCount)
  const dateStatusByKey = computed(() =>
    getMealPlanDateStatuses(publishDatesData.value?.dates, selectionSpanInDays.value),
  )
  const mutation = useMutation({
    mutationFn: async (shouldUnpublish: boolean) => {
      if (shouldUnpublish) {
        await dietsService.unpublishDiet(props.dietId)
      }
      return dietsService.updateStartDate(
        props.dietId,
        G.isDate(modalStartDate.value) && !allowPatientToChooseStartDate.value
          ? format(modalStartDate.value, 'yyyy-MM-dd')
          : undefined,
      )
    },
    onError: error => {
      globalStore.createMessage({ title: 'Błąd podczas aktualizacji daty' })
      reportError(error, 'Error while updating start date', {
        dietId: props.dietId,
      })
    },
    onSuccess: () => {
      globalStore.createMessage({ title: 'Data jadłospisu została zmieniona' })
      void queryClient.invalidateQueries({ queryKey: ['patientMealPlans'] })
      emit('success')
    },
  })

  function getDateStatus(date: Date): MealPlanDateStatus | null {
    return dateStatusByKey.value.get(toMealPlanDateKey(date)) ?? null
  }

  function isDateDisabled(date: Date) {
    return dateStatusByKey.value.has(toMealPlanDateKey(date))
  }

  function clearDates() {
    modalStartDate.value = undefined
    allowPatientToChooseStartDate.value = false
  }

  function save() {
    if (
      !modalStartDate.value &&
      props.isSharedWithPatient &&
      !allowPatientToChooseStartDate.value
    ) {
      confirmationOpen.value = true
      return
    }
    mutation.mutate(false)
  }
</script>

<style scoped>
  .change-diet-dates-modal__title {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .change-diet-dates-modal__title-icon {
    flex-shrink: 0;
  }

  .change-diet-dates-modal__body {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .change-diet-dates-modal__description {
    margin: 0;
    font-size: 12px;
    color: var(--color-tertiary-text);
  }

  .change-diet-dates-modal__date-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }

  .change-diet-dates-modal__legend {
    align-self: flex-end;
    margin-right: 4px;
  }

  .change-diet-dates-modal__date-picker-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .change-diet-dates-modal__loader-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(255 255 255 / 80%);
    border-radius: 8px;
  }

  .change-diet-dates-modal__switch-row {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .change-diet-dates-modal__switch-label {
    font-size: 14px;
    color: var(--color-primary-text);
  }

  .change-diet-dates-modal__warning-description {
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-tertiary-text);
  }
</style>

<style>
  .change-diet-dates-modal {
    width: 500px;
    overflow-y: auto;
  }
</style>
